/* Shared aggregation/derivation helpers for the Reports tab + Dashboard
 * Source Performance card. All inputs are the "active non-test" leads list
 * (already filtered upstream — archived/trashed/test leads excluded). */

const BUSINESS_TZ = "America/New_York";

/* YYYY-MM-DD in NY timezone. */
export function nyDateStr(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

function leadCreatedNyDate(lead) {
  if (!lead?.created_at) return "";
  try { return nyDateStr(new Date(lead.created_at)); } catch { return ""; }
}

/* ── Time window helpers (used by toggle in Reports tab) ─────────────────── */
export const TIME_WINDOWS = [
  { v: "all", label: "All time" },
  { v: "month", label: "This month" },
  { v: "30d", label: "Last 30 days" },
  { v: "90d", label: "Last 90 days" },
];

/* Returns { from, to } as YYYY-MM-DD strings in NY tz (inclusive). null = no bound. */
export function timeWindowRange(key) {
  const today = nyDateStr();
  if (key === "all" || !key) return { from: null, to: null };
  if (key === "month") return { from: today.slice(0, 7) + "-01", to: today };
  const days = key === "30d" ? 30 : key === "90d" ? 90 : 0;
  if (!days) return { from: null, to: null };
  const base = new Date(`${today}T12:00:00`);
  const start = new Date(base);
  start.setDate(base.getDate() - (days - 1));
  return { from: nyDateStr(start), to: today };
}

export function filterByWindow(leads, key) {
  const { from, to } = timeWindowRange(key);
  if (!from && !to) return leads;
  return leads.filter((l) => {
    const d = leadCreatedNyDate(l);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
}

/* ── Aggregators ────────────────────────────────────────────────────────── */

/* Top-N + "Other" rollup. Returns array of { key, count } sorted desc. */
export function topNWithOther(rows, n) {
  if (rows.length <= n) return rows;
  const top = rows.slice(0, n);
  const otherCount = rows.slice(n).reduce((s, r) => s + r.count, 0);
  if (otherCount > 0) top.push({ key: "Other", count: otherCount, _isOther: true });
  return top;
}

function groupCount(leads, keyFn) {
  const map = new Map();
  for (const l of leads) {
    const k = keyFn(l);
    if (!k) continue;
    map.set(k, (map.get(k) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

export function leadsBySource(leads) {
  return groupCount(leads, (l) => l.source_label || "Direct / Unknown");
}

export function leadsByCampaign(leads) {
  return groupCount(leads, (l) => {
    const c = (l.utm_campaign || "").trim();
    if (!c) return "(no campaign)";
    return c;
  });
}

export function leadsByStatus(leads) {
  // Normalize legacy `statement_needed` to `info_needed` so old + new leads
  // roll up into the same bucket in Reports.
  return groupCount(leads, (l) => {
    const s = l.status || "new";
    return s === "statement_needed" ? "info_needed" : s;
  });
}

export function leadsByPriority(leads) {
  return groupCount(leads, (l) => l.priority || "warm");
}

/* Onboarding breakdown — mirrors DashboardTab logic. */
const ONBOARDING_REQUIRED = ["personal_id", "ssn", "business_tax_id", "voided_check", "personal_phone", "business_phone", "email"];
const ONBOARDING_ALL = [...ONBOARDING_REQUIRED, "ebt_license"];
export function onboardingStateOf(lead) {
  const info = lead?.onboarding_info || {};
  const requiredResolved = ONBOARDING_REQUIRED.every((k) => {
    const s = info[k]?.status;
    return s === "received" || s === "not_applicable";
  });
  const anyTouched = ONBOARDING_ALL.some((k) => info[k] && info[k].status && info[k].status !== "needed");
  if (requiredResolved && anyTouched) return "submitted";
  if (anyTouched) return "in_progress";
  return "none";
}
export function leadsByOnboarding(leads) {
  return groupCount(leads, onboardingStateOf);
}

/* Leads by month (YYYY-MM) — fills last `months` periods including current,
 * even if zero, so the chart renders a continuous timeline. */
export function leadsByMonth(leads, months = 12) {
  const todayStr = nyDateStr();
  const [yStr, mStr] = todayStr.split("-");
  const baseYear = Number(yStr);
  const baseMonth = Number(mStr);
  // Build period skeleton ordered oldest -> newest.
  const periods = [];
  for (let i = months - 1; i >= 0; i--) {
    const dt = new Date(Date.UTC(baseYear, baseMonth - 1 - i, 1));
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
    periods.push(`${y}-${m}`);
  }
  const counts = new Map(periods.map((p) => [p, 0]));
  for (const l of leads) {
    const d = leadCreatedNyDate(l);
    if (!d) continue;
    const k = d.slice(0, 7);
    if (counts.has(k)) counts.set(k, counts.get(k) + 1);
  }
  return periods.map((p) => ({ month: p, count: counts.get(p) || 0 }));
}

/* Per-source/per-campaign KPI rows for Source Performance card. */
export function sourcePerformance(leads, top = 5) {
  const rows = leadsBySource(leads);
  return rows.slice(0, top);
}

/* Labels — `statement_needed` mapped to "Info Needed" for legacy data. */
export const STATUS_LABELS = {
  new: "New Lead",
  info_needed: "Info Needed",
  statement_needed: "Info Needed",
  contacted: "Contacted",
  proposal_sent: "Proposal Sent",
  follow_up: "Follow-Up",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};
export const PRIORITY_LABELS = { hot: "Hot Lead", warm: "Warm Lead", cold: "Cold Lead" };
export const ONBOARDING_LABELS = {
  none: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
};
/* Status pie/donut colors. Aligned to the progress system: yellow=missing info,
 * greens grow stronger toward Won, dark red=Lost. Legacy `statement_needed`
 * inherits the new yellow so old leads render consistently in Reports. */
export const STATUS_COLORS = {
  new: "#0072FF",
  info_needed: "#FACC15",
  statement_needed: "#FACC15",
  contacted: "#BBF7D0",
  proposal_sent: "#86EFAC",
  follow_up: "#22C55E",
  closed_won: "#15803D",
  closed_lost: "#991B1B",
};
export const PRIORITY_COLORS = { hot: "#F43F5E", warm: "#F59E0B", cold: "#94A3B8" };
export const ONBOARDING_COLORS = { none: "#94A3B8", in_progress: "#0072FF", submitted: "#10B981" };

export const MONTH_LABELS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function prettyMonth(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return yyyyMm || "";
  const [y, m] = yyyyMm.split("-");
  return `${MONTH_LABELS_SHORT[Number(m) - 1]} ${y}`;
}
export function prettyMonthShort(yyyyMm) {
  if (!yyyyMm || !/^\d{4}-\d{2}$/.test(yyyyMm)) return yyyyMm || "";
  const [, m] = yyyyMm.split("-");
  return MONTH_LABELS_SHORT[Number(m) - 1];
}
